import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { getProspects } from '../services/prospects'
import { prospectNeedsAction } from '../utils/prospectHelpers'
import { getIssuesBadgeData } from '../services/issues'
import { requiresLandlordAction } from '../utils/issueActions'
import { getExpiringDocuments } from '../services/compliance'
import { getDocumentsToServeCount } from '../services/tenancies'
import { getUpcomingScheduledMaintenance } from '../services/scheduledMaintenance'
import { getMyUnreadMessageCount } from '../services/tenancyMessages'

interface BadgeCounts {
  prospects: number
  maintenance: number
  compliance: number
  documentsToServe: number
  messages: number
}

export function useBadgeCounts() {
  const location = useLocation()
  const [badgeCounts, setBadgeCounts] = useState<BadgeCounts>({
    prospects: 0,
    maintenance: 0,
    compliance: 0,
    documentsToServe: 0,
    messages: 0,
  })

  // Fetch badge counts from real data
  useEffect(() => {
    const fetchBadgeCounts = async () => {
      try {
        const [prospects, issuesBadgeData, expiringDocs, docsToServe, smTasks, unreadMessages] = await Promise.all([
          getProspects().catch(() => []),
          getIssuesBadgeData().catch(() => []),
          getExpiringDocuments(30).catch(() => []),
          getDocumentsToServeCount().catch(() => 0),
          getUpcomingScheduledMaintenance(7).catch(() => []),
          getMyUnreadMessageCount().catch(() => 0),
        ])

        // Count prospects that actually need landlord action
        const prospectsNeedingAction = prospects.filter(
          p => !['converted', 'rejected', 'withdrawn'].includes(p.status) && prospectNeedsAction(p)
        ).length

        // Count issues requiring landlord action + overdue scheduled maintenance
        const actionNeededIssues = issuesBadgeData.filter(
          i => requiresLandlordAction(i as Parameters<typeof requiresLandlordAction>[0])
        ).length
        const maintenanceTotal = actionNeededIssues + smTasks.length

        // Expiring/expired compliance docs count
        const complianceIssues = expiringDocs.length

        setBadgeCounts({
          prospects: prospectsNeedingAction,
          maintenance: maintenanceTotal,
          compliance: complianceIssues,
          documentsToServe: docsToServe,
          messages: unreadMessages,
        })
      } catch {
        // Silently fail - badges will show 0
      }
    }

    fetchBadgeCounts()
  }, [location.pathname]) // Refresh when navigating

  // Get dynamic badge count for a route
  const getBadgeCount = (route: string): number | null => {
    switch (route) {
      case '/prospects':
        return badgeCounts.prospects || null
      case '/maintenance':
        return badgeCounts.maintenance || null
      case '/compliance':
        return badgeCounts.compliance || null
      case '/tenants':
        return (badgeCounts.documentsToServe + badgeCounts.messages) || null
      default:
        return null
    }
  }

  return { badgeCounts, getBadgeCount }
}
